import { useExperimentation } from '@user-portal/experimentation';
import { useCallback, useEffect } from 'react';

import { fetchFeatures, setResolvedFlags } from 'store/features/actions';
import { selectIsNavReady } from 'store/features/selectors';

import { useDispatch, useSelector } from 'hooks';

import { collectFlagKeys } from './collectFlagKeys';
import { NAV_ITEMS } from './config';
import { ResolvedFlags } from './types';

/**
 * Imperative flag resolution (off `asyncClient`, avoids rules-of-hooks) plus
 * `/features` fetch. Fires once high up (inside `ExperimentationWrapper`) when
 * auth completes — i.e. on app open / page refresh.
 */
export const useBootstrapNavigation = (): { isNavReady: boolean } => {
    const dispatch = useDispatch();
    const { asyncClient, subjectAttributes } = useExperimentation();
    const isAuthComplete = useSelector((state) => state.auth.isComplete);
    const isNavReady = useSelector(selectIsNavReady);

    const refresh = useCallback(async () => {
        try {
            const client = await asyncClient;
            const values: ResolvedFlags = {};

            collectFlagKeys(NAV_ITEMS).forEach(({ key, type }) => {
                values[key] =
                    type === 'json'
                        ? client.getJSONAssignment(key, subjectAttributes, {})
                        : client.getBooleanAssignment(key, subjectAttributes, false);
            });

            dispatch(setResolvedFlags(values));
        } catch {
            dispatch(setResolvedFlags({}));
        }

        dispatch(fetchFeatures());
    }, [asyncClient, subjectAttributes, dispatch]);

    useEffect(() => {
        if (!isAuthComplete) {
            return;
        }

        refresh();
    }, [isAuthComplete, refresh]);

    return { isNavReady };
};
